import { readonly, ref } from 'vue'
import { getPlatformSetting } from '@/services/platformSetting'
import type { RawPlatformSetting } from '@/types/api'

/**
 * Reassurance screen shown on a visitor's first arrival on MySmerra, confirming
 * they are on the official Smerra space that replaces the old one.
 *
 * Display rule — the screen is visible if and only if:
 *  - the backend switch `reassurance_screen_enabled` is `true`, AND
 *  - this browser has not dismissed it yet (`localStorage` flag).
 *
 * The settings are fetched at most once per browser session: the result is
 * kept in memory for in-app navigation and in `sessionStorage` across reloads.
 * Any failure (network, 5xx, unreadable body) hides the screen silently: it is
 * a nice-to-have and must never block or break the journey.
 *
 * When `localStorage` is unavailable (private mode, blocked storage), the
 * dismissal is remembered in memory instead, so the screen shows once per
 * session rather than on every navigation.
 *
 * Module-level singleton: every caller shares the same state. Browser-only —
 * call `load()` from a client hook (`onMounted`), never during SSG.
 */

/** Persistent per-browser flag set once the screen has been dismissed. */
export const REASSURANCE_SEEN_KEY = 'mysmerra_reassurance_seen'
/** Per-session cache of the platform settings, so a reload does not refetch. */
const SETTING_CACHE_KEY = 'mysmerra_platform_setting'

const visible = ref(false)
/** Fallback dismissal flag when `localStorage` cannot be written. */
let seenInMemory = false
/** In-flight or settled fetch, shared so concurrent callers hit the API once. */
let settingRequest: Promise<RawPlatformSetting> | null = null

function hasBeenSeen(): boolean {
  if (seenInMemory) return true
  try {
    return window.localStorage.getItem(REASSURANCE_SEEN_KEY) === '1'
  } catch {
    return false
  }
}

function markAsSeen(): void {
  seenInMemory = true
  try {
    window.localStorage.setItem(REASSURANCE_SEEN_KEY, '1')
  } catch {
    /* storage unavailable: the in-memory flag covers the rest of the session */
  }
}

function readCachedSetting(): RawPlatformSetting | null {
  try {
    const raw = window.sessionStorage.getItem(SETTING_CACHE_KEY)
    return raw ? (JSON.parse(raw) as RawPlatformSetting) : null
  } catch {
    return null
  }
}

function writeCachedSetting(setting: RawPlatformSetting): void {
  try {
    window.sessionStorage.setItem(SETTING_CACHE_KEY, JSON.stringify(setting))
  } catch {
    /* no cache: the in-memory request still prevents refetching on navigation */
  }
}

/** The platform settings, from the session cache or a single shared request. */
function loadPlatformSetting(): Promise<RawPlatformSetting> {
  if (!settingRequest) {
    const cached = readCachedSetting()
    settingRequest = cached
      ? Promise.resolve(cached)
      : getPlatformSetting().then((setting) => {
          writeCachedSetting(setting)
          return setting
        })
    // A failed request is not cached: the next app start may try again.
    settingRequest.catch(() => {
      settingRequest = null
    })
  }
  return settingRequest
}

/** Decide whether to show the screen. Resolves once decided; never throws. */
async function load(): Promise<void> {
  if (hasBeenSeen()) return
  try {
    const setting = await loadPlatformSetting()
    visible.value = setting.reassurance_screen_enabled === true && !hasBeenSeen()
  } catch (error) {
    visible.value = false
    if (import.meta.env.DEV) console.warn('[reassurance] platform setting unavailable', error)
  }
}

/** Close the screen and remember it so it is not shown again. */
function dismiss(): void {
  visible.value = false
  markAsSeen()
}

export function useReassuranceScreen() {
  return { visible: readonly(visible), load, dismiss }
}
